import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Crown, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  useSaveMentorshipGroup, useUserSearch, usePeopleByIds,
  type MentorshipGroup, type MentorshipPerson,
} from '@/hooks/useCustomMentorships';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  organizationId: string;
  /** Horas contratadas del proyecto y horas ya asignadas a otros grupos. */
  contractedHours: number;
  assignedByOtherGroups: number;
  group?: MentorshipGroup | null;
  /** Mentor líder de la mentoría: si el grupo no tiene apoyo, lo atiende él. */
  leadMentor: { id: string; name: string } | null;
  /** Mentores de apoyo de la mentoría. Con el líder, los que pueden ser apoyo del grupo. */
  supportMentors: { id: string; name: string }[];
  /** Mentores de la plataforma: ninguno puede quedar como líder del proyecto. */
  vivetoriIds: string[];
}

const VIVETORI_AS_LEADER_ERROR =
  'Alguien de Vivetori no puede ser el líder del proyecto: el líder es el responsable por parte del cliente.';

export function GroupFormDialog({
  open, onOpenChange, projectId, organizationId, contractedHours, assignedByOtherGroups, group,
  leadMentor, supportMentors, vivetoriIds,
}: Props) {
  const save = useSaveMentorshipGroup();
  const [form, setForm] = useState({
    name: '', group_type: '', objective: '',
    diag_current_situation: '', diag_gap: '', diag_expected_result: '', diag_indicator: '',
    estimated_hours: '0',
  });
  const [supportMentorId, setSupportMentorId] = useState<string>('');
  const [participants, setParticipants] = useState<{ user_id: string; is_leader: boolean }[]>([]);
  const [term, setTerm] = useState('');
  const [onlyOrg, setOnlyOrg] = useState(true);

  // Quienes pueden ser apoyo del grupo: los mentores de esa mentoría (apoyos y líder).
  const candidates = [
    ...supportMentors.map((m) => ({ ...m, role: 'apoyo de la mentoría' })),
    ...(leadMentor ? [{ ...leadMentor, role: 'mentor líder' }] : []),
  ];

  // Se leen al abrir el diálogo sin ser dependencia del efecto: si lo fueran, cada
  // re-render de la página (con listas nuevas) reiniciaría el formulario a medio llenar.
  const latestCandidateIds = useRef<string[]>([]);
  latestCandidateIds.current = candidates.map((m) => m.id);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: group?.name ?? '',
      group_type: group?.group_type ?? '',
      objective: group?.objective ?? '',
      diag_current_situation: group?.diag_current_situation ?? '',
      diag_gap: group?.diag_gap ?? '',
      diag_expected_result: group?.diag_expected_result ?? '',
      diag_indicator: group?.diag_indicator ?? '',
      estimated_hours: String(group?.estimated_hours ?? 0),
    });
    // Grupo nuevo: el apoyo de la mentoría si existe; si no, el mentor líder.
    // Grupo existente: su apoyo actual, salvo que ya no sea mentor de la mentoría.
    const candidateIds = latestCandidateIds.current;
    const initial = group ? group.support_mentor_id : candidateIds[0] ?? null;
    setSupportMentorId(initial && candidateIds.includes(initial) ? initial : '');
    setParticipants(
      (group?.mentorship_group_participants ?? []).map((p) => ({ user_id: p.user_id, is_leader: p.is_leader }))
    );
    setTerm('');
    setOnlyOrg(true);
  }, [open, group]);

  const { data: results = [], isFetching } = useUserSearch(term, organizationId, onlyOrg);
  const selectedIds = participants.map((p) => p.user_id);
  const { data: selectedPeople = [] } = usePeopleByIds(selectedIds);
  const peopleById = useMemo(() => {
    const map = new Map<string, MentorshipPerson>();
    [...selectedPeople, ...results].forEach((p) => map.set(p.id, p));
    return map;
  }, [selectedPeople, results]);

  const remainingForGroups = contractedHours - assignedByOtherGroups;

  // El líder del proyecto es del cliente: nunca un mentor ni alguien con correo de Vivetori.
  const isVivetori = (userId: string, email?: string | null) =>
    vivetoriIds.includes(userId) || (email ?? peopleById.get(userId)?.email ?? '').toLowerCase().endsWith('@vivetori.com');

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('El nombre del grupo es obligatorio');
      return;
    }
    if (Number(form.estimated_hours) > remainingForGroups) {
      toast.error(`Las horas estimadas superan las disponibles (${remainingForGroups} h)`);
      return;
    }
    if (supportMentorId && !candidates.some((m) => m.id === supportMentorId)) {
      toast.error('El mentor de apoyo del grupo debe ser un mentor de esta mentoría.');
      return;
    }
    if (participants.some((p) => p.is_leader && isVivetori(p.user_id))) {
      toast.error(VIVETORI_AS_LEADER_ERROR);
      return;
    }
    try {
      await save.mutateAsync({
        id: group?.id,
        project_id: projectId,
        name: form.name.trim(),
        group_type: form.group_type,
        objective: form.objective,
        diag_current_situation: form.diag_current_situation,
        diag_gap: form.diag_gap,
        diag_expected_result: form.diag_expected_result,
        diag_indicator: form.diag_indicator,
        estimated_hours: Number(form.estimated_hours || 0),
        support_mentor_id: supportMentorId || null,
        participants,
      } as any);
      toast.success(group ? 'Grupo actualizado' : 'Grupo creado');
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'No se pudo guardar el grupo');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{group ? 'Editar grupo' : 'Nuevo grupo'}</DialogTitle>
          <DialogDescription>
            Horas disponibles para asignar a grupos: {remainingForGroups} de {contractedHours}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Nombre *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Mentor de apoyo del grupo</Label>
            <Select value={supportMentorId || 'none'} onValueChange={(v) => setSupportMentorId(v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  Sin apoyo · lo atiende el mentor líder{leadMentor ? ` (${leadMentor.name})` : ''}
                </SelectItem>
                {candidates.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name} ({m.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Quien normalmente da las sesiones del grupo. Si no puede, la da el líder u otro mentor.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Input value={form.group_type} onChange={(e) => setForm({ ...form, group_type: e.target.value })} placeholder="Ej. Área comercial" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Objetivo</Label>
            <Textarea rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Situación actual</Label>
            <Textarea rows={2} value={form.diag_current_situation} onChange={(e) => setForm({ ...form, diag_current_situation: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Brecha</Label>
            <Textarea rows={2} value={form.diag_gap} onChange={(e) => setForm({ ...form, diag_gap: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Resultado esperado</Label>
            <Textarea rows={2} value={form.diag_expected_result} onChange={(e) => setForm({ ...form, diag_expected_result: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Indicador</Label>
            <Textarea rows={2} value={form.diag_indicator} onChange={(e) => setForm({ ...form, diag_indicator: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Horas estimadas</Label>
            <Input type="number" min={0} step="0.5" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} />
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <Label>Participantes</Label>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Solo la empresa del proyecto
              <Switch checked={onlyOrg} onCheckedChange={setOnlyOrg} />
            </label>
          </div>
          <p className="text-xs text-muted-foreground -mt-1">
            Personas del cliente que reciben la mentoría. Una de ellas es el <strong>líder del proyecto</strong>:
            el responsable por parte del cliente (nunca alguien de Vivetori).
          </p>

          {participants.length === 0 ? (
            <p className="text-sm text-muted-foreground rounded-md border border-dashed p-3">
              Aún no hay participantes. Búscalos abajo y agrégalos.
            </p>
          ) : (
            <div className="rounded-md border divide-y">
              {participants.map((p) => {
                const person = peopleById.get(p.user_id);
                return (
                  <div key={p.user_id} className={`flex items-center justify-between gap-3 p-2 ${p.is_leader ? 'bg-primary/5' : ''}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1">
                        {p.is_leader && <Crown className="w-3.5 h-3.5 text-primary shrink-0" />}
                        {person?.full_name || person?.email || 'Usuario'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.is_leader ? 'Líder del proyecto · responsable por parte del cliente' : 'Participante'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!p.is_leader && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => {
                            if (isVivetori(p.user_id)) {
                              toast.error(VIVETORI_AS_LEADER_ERROR);
                              return;
                            }
                            setParticipants(participants.map((x) => ({ ...x, is_leader: x.user_id === p.user_id })));
                          }}
                        >
                          <Crown className="w-3 h-3 mr-1" /> Hacer líder
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        title="Quitar participante"
                        onClick={() => setParticipants(participants.filter((x) => x.user_id !== p.user_id))}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {participants.length > 0 && !participants.some((p) => p.is_leader) && (
            <p className="text-xs text-amber-700 dark:text-amber-400">Falta marcar al líder del proyecto.</p>
          )}

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={onlyOrg ? 'Buscar en la empresa del proyecto…' : 'Escribe al menos 2 letras del nombre o correo…'}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
          <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
            {isFetching && <div className="p-3 text-sm text-muted-foreground">Buscando…</div>}
            {!isFetching && results.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground">Sin resultados.</div>
            )}
            {results
              .filter((r) => !selectedIds.includes(r.id))
              .map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="w-full text-left p-2 hover:bg-muted text-sm"
                  onClick={() =>
                    // El primero en agregarse queda como líder solo si no es de Vivetori.
                    setParticipants([
                      ...participants,
                      { user_id: r.id, is_leader: participants.length === 0 && !isVivetori(r.id, r.email) },
                    ])
                  }
                >
                  <span className="font-medium">{r.full_name || 'Sin nombre'}</span>
                  <span className="text-muted-foreground"> · {r.email}</span>
                </button>
              ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={save.isPending}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
